import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { ApiError } from '../api/client';
import { getActivation, setOffering, skipFirstItem } from '../api/onboarding';
import { InfoDialog, type InfoTopic } from '../info/InfoDialog';
import { publicSupportContext } from '../info/supportInfo';
import { useInfoDialogBrowserBack } from '../info/useInfoDialogBrowserBack';
import { noteOnboardingError, noteOnboardingErrorCode } from '../utils/onboardingTelemetry';
import { QuickProductForm } from '../components/products/QuickProductForm';
import { Icon } from '../components/ui/Icon';
import { Button, Card, Field, Input, LoadingPanel, Select } from '../components/ui/primitives';
import { Wordmark } from '../layouts/AppShell';
import type { Activation, BusinessOffering } from '../types/onboarding';
import { ACCOUNT_PASSWORD_HINT, accountPasswordProblem } from '../utils/passwordPolicy';
import { offeringNoun } from '../utils/quickProduct';
import {
  acceptInvite, claimInvite, completeOnboarding, configureFirstStore, continueSignup, getInviteSession, getOnboarding, getSignupSession, inspectInvite,
  resendVerification, SelfServiceApiError, setupBusiness, setupInviteBusiness, startSignup, verifySignup,
  type OnboardingStatus, type SessionView, type SetupBusinessBody, type SetupBusinessResult, type SignupView as RegistrationView,
} from './api';
import {
  continueErrorMessage, detectTimezone, firstNameOf, inviteErrorMessage, parseSelfServiceHash, setupErrorMessage, signupErrorMessage,
  suggestTimezone, timezoneChoices, timezoneHint, verifyErrorMessage, type SelfServiceView,
} from './format';
import { signupHandoff } from './handoff';
import { countryOption, currencyChoices, LOCALE_SCOPE_NOTE, suggestCountry, SUPPORTED_COUNTRIES } from './locale';
import { usePublicConfig } from './usePublicConfig';
import { POS_HASH } from '../utils/newProductRoute';

// Fase 27B: experiencia de registro autoservicio. ONB-001 (Macrofase A) la extiende —no la reemplaza—
// hasta la primera venta, con la piel del rediseño (primitives de los Bloques A–D, sin marca nueva):
//
//   Crear cuenta (nombre, correo, contraseña) → Revisa tu correo → (enlace) correo confirmado
//   → Bienvenida → Prepara tu negocio (negocio, primera sucursal, país, moneda y zona horaria)
//   → ¿Qué vas a vender? → Primer artículo (opcional) → Ya puedes empezar → punto de venta o Inicio.
//
// El backend es la fuente de verdad de cada paso. ONB-002: no hay PIN ni ningún segundo secreto. La
// contraseña se escribe una vez, al crear la cuenta, y es la del dueño. Entre pasos manda la sesión corta
// de registro, una cookie httpOnly que pone el servidor (este código no la ve). ONB-003: abrir el enlace
// le da esa sesión al navegador que lo abre (y se la quita a cualquier otro); si no es el que creó la
// cuenta, ahí se sigue con la contraseña. Después de verificar, otro equipo solo sigue abriendo un enlace
// nuevo. Aquí no se guarda nada en el navegador. El signup público sigue apagado por default: con el gate
// apagado el servidor responde 404 y esta pantalla lo dice.

const RESEND_COOLDOWN_SECONDS = 60; // espejo del cooldown del backend (signupLimiter)
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function SelfServiceApp() {
  const [route, setRoute] = useState<SelfServiceView>(parseSelfServiceHash(window.location.hash));
  useEffect(() => {
    // ONB-010: la invitación borra su token de la dirección al leerlo; esa dirección sin token sigue siendo la MISMA
    // invitación (no se reinicia la pantalla). Solo un token nuevo es otra invitación.
    const onHash = () => setRoute((prev) => {
      const next = parseSelfServiceHash(window.location.hash);
      // P-085 (lo encontró el E2E; venía de ONB-010): el MISMO enlace abierto otra vez en la misma pestaña no vuelve a
      // montar la pantalla, así que el token se quedaba en la dirección. Se quita aquí también.
      if (next.view === 'invite' && next.token !== null && prev.view === 'invite' && prev.token === next.token) {
        try { history.replaceState(null, '', `${window.location.pathname}${window.location.search}#beta-invite`); } catch { /* noop */ }
        return prev;
      }
      return next.view === 'invite' && next.token === null && prev.view === 'invite' ? prev : next;
    });
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  return (
    <main className="n3-onb">
      <div className="n3-onb__column">
        <div className="n3-onb__brand"><Wordmark /></div>
        {route.view === 'signup' && <SignupView />}
        {route.view === 'verify' && <VerifyView token={route.token} />}
        {route.view === 'continue' && <ContinueView />}
        {route.view === 'setup' && <SetupView />}
        {/* Un enlace NUEVO en la misma pestaña (solo cambia el hash) es otra invitación: se vuelve a abrir desde cero. */}
        {route.view === 'invite' && <InviteView key={route.token ?? ''} token={route.token} />}
        {route.view === 'invite-setup' && <SetupView flow={INVITE_FLOW} />}
        {route.view === 'onboarding' && <OnboardingView />}
        <InfoLinks />
      </div>
    </main>
  );
}

/**
 * P-085: Ayuda · Privacidad · Términos, al pie de cada paso. Se abren en un diálogo en la misma pantalla: la invitación
 * guarda su enlace solo en memoria y cambiar de pantalla lo perdería (Codex R1, K2).
 */
function InfoLinks() {
  const [topic, setTopic] = useState<InfoTopic | null>(null);
  // Codex R2-03: en el Chrome del teléfono, Atrás cierra la ayuda y la invitación sigue (su enlace vive en memoria).
  useInfoDialogBrowserBack(topic !== null);
  return (
    <>
      <nav aria-label="Ayuda e información" className="n3-onb__foot">
        <Button className="n3-onb__link" onClick={() => setTopic('help')} variant="ghost">Ayuda</Button>
        <Button className="n3-onb__link" onClick={() => setTopic('privacy')} variant="ghost">Privacidad</Button>
        <Button className="n3-onb__link" onClick={() => setTopic('terms')} variant="ghost">Términos</Button>
      </nav>
      {topic && <InfoDialog context={publicSupportContext(window.location.hash)} initial={topic} onClose={() => setTopic(null)} />}
    </>
  );
}

const goTo = (hash: string) => { window.location.hash = hash; };
const goLogin = () => goTo('');

// ── Piezas comunes ─────────────────────────────────────────────────────────────────────────────

/** Una pantalla del recorrido: título (recibe el foco al aparecer, para el lector de pantalla) y cuerpo. */
function Step({ id, title, lead, eyebrow, children }: { id: string; title: string; lead?: ReactNode; eyebrow?: ReactNode; children?: ReactNode }) {
  const titleRef = useRef<HTMLHeadingElement>(null);
  // Si un campo ya pidió el foco (autoFocus), se respeta; si no, el título lo recibe.
  useEffect(() => {
    const active = document.activeElement;
    if (!active || active === document.body) titleRef.current?.focus();
  }, [id]);
  return (
    <Card aria-labelledby={`onb-${id}`} as="section" className="n3-onb__panel">
      {eyebrow && <p className="n3-onb__eyebrow">{eyebrow}</p>}
      <h1 className="n3-onb__title" id={`onb-${id}`} ref={titleRef} tabIndex={-1}>{title}</h1>
      {lead && <div className="n3-onb__lead">{lead}</div>}
      {children}
    </Card>
  );
}

function Alert({ children }: { children: ReactNode }) {
  return <p className="n3-onb__alert" role="alert"><Icon name="warning" size={18} /><span>{children}</span></p>;
}

function Notice({ children }: { children: ReactNode }) {
  return <p className="n3-onb__notice" role="status"><Icon name="check" size={18} /><span>{children}</span></p>;
}

function TextLink({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return <Button className="n3-onb__link" onClick={onClick} variant="ghost">{children}</Button>;
}

function Loading({ label = 'Cargando…' }: { label?: string }) {
  return <Card className="n3-onb__panel"><LoadingPanel label={label} lines={4} /></Card>;
}

// La contraseña de la cuenta, con mostrar/ocultar. Nunca se guarda en el navegador. Al crearla se dicen
// los requisitos; al escribirla para seguir, no (la compara el servidor).
function PasswordField({ value, onChange, error, label = 'Contraseña', autoComplete, autoFocus }: {
  value: string; onChange: (value: string) => void; error?: string; label?: string; autoComplete: 'new-password' | 'current-password'; autoFocus?: boolean;
}) {
  const [show, setShow] = useState(false);
  return (
    <Field error={error} hint={autoComplete === 'new-password' ? ACCOUNT_PASSWORD_HINT : undefined} label={label} required>
      <Input
        autoCapitalize="none"
        autoComplete={autoComplete}
        autoCorrect="off"
        autoFocus={autoFocus}
        maxLength={72}
        onChange={(event) => onChange(event.target.value)}
        spellCheck={false}
        suffix={(
          <button aria-label={show ? 'Ocultar contraseña' : 'Mostrar contraseña'} aria-pressed={show} className="n3-onb__reveal" onClick={() => setShow((s) => !s)} type="button">
            {show ? 'Ocultar' : 'Mostrar'}
          </button>
        )}
        type={show ? 'text' : 'password'}
        value={value}
      />
    </Field>
  );
}

const statusOf = (error: unknown) => (error instanceof SelfServiceApiError ? error.status : -1);
const codeOf = (error: unknown) => (error instanceof SelfServiceApiError ? error.code : undefined);

// ── 1. Crear cuenta ────────────────────────────────────────────────────────────────────────────

function SignupView() {
  const { config, loading } = usePublicConfig();
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [tried, setTried] = useState(false);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const errors = {
    name: displayName.trim().length < 2 ? 'Escribe tu nombre (al menos 2 letras).' : undefined,
    email: !EMAIL_PATTERN.test(email.trim()) ? 'Escribe un correo válido.' : undefined,
    password: accountPasswordProblem(password),
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setTried(true);
    if (errors.name || errors.email || errors.password) return;
    setBusy(true); setError(undefined);
    try {
      await startSignup({ displayName: displayName.trim(), email: email.trim(), password });
      setPassword(''); // la contraseña no se queda en la pantalla
      setSent(true);
    } catch (err) {
      setError(signupErrorMessage(statusOf(err), codeOf(err)));
    } finally { setBusy(false); }
  };

  if (loading) return <Loading />;

  // Gate: el registro no está disponible (servidor apagado o sin correo). No se muestra el formulario.
  if (!config?.signupEnabled) {
    return (
      <Step id="closed" lead="El registro de nuevos negocios no está abierto en este momento." title="Crear cuenta">
        <div className="n3-onb__actions"><Button fullWidth onClick={goLogin} size="lg" variant="primary">Ir a iniciar sesión</Button></div>
      </Step>
    );
  }

  if (sent) return <CheckEmailView email={email.trim()} onChangeEmail={() => { setSent(false); setTried(false); }} />;

  return (
    <Step id="signup" lead="Tu nombre, tu correo y una contraseña. Con esa contraseña entrarás a tu negocio. Te enviaremos un correo para confirmarlo." title="Crea tu cuenta">
      <form className="n3-onb__form" noValidate onSubmit={(event) => void submit(event)}>
        {error && <Alert>{error}</Alert>}
        <Field error={tried ? errors.name : undefined} label="Tu nombre" required>
          <Input autoComplete="name" maxLength={120} onChange={(event) => setDisplayName(event.target.value)} value={displayName} />
        </Field>
        <Field error={tried ? errors.email : undefined} label="Correo" required>
          <Input autoComplete="email" inputMode="email" maxLength={100} onChange={(event) => setEmail(event.target.value)} type="email" value={email} />
        </Field>
        <PasswordField autoComplete="new-password" error={tried ? errors.password : undefined} onChange={setPassword} value={password} />
        <div className="n3-onb__actions">
          <Button fullWidth loading={busy} loadingLabel="Enviando…" size="lg" type="submit" variant="primary">Crear cuenta</Button>
        </div>
      </form>
      <p className="n3-onb__foot">¿Ya tienes cuenta? <TextLink onClick={goLogin}>Inicia sesión</TextLink></p>
    </Step>
  );
}

// ── 2. Revisa tu correo ────────────────────────────────────────────────────────────────────────

/**
 * «Continuar aquí» pregunta al servidor en qué punto está la solicitud de este navegador (su sesión corta
 * de registro): si el correo ya quedó confirmado (en este u otro equipo), sigue a preparar el negocio; si
 * no hay sesión, pide correo y contraseña. `onVerified` / `onNeedPassword` sustituyen a la navegación
 * cuando la pantalla YA está en esa dirección (volver a escribir el mismo hash no avisa a nadie).
 */
function CheckEmailView({ email, onChangeEmail, onVerified, onNeedPassword }: {
  email: string; onChangeEmail?: () => void; onVerified?: () => void; onNeedPassword?: () => void;
}) {
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  const [notice, setNotice] = useState<string>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setInterval(() => setCooldown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const resend = async () => {
    if (busy || cooldown > 0) return;
    setBusy(true); setError(undefined); setNotice(undefined);
    try {
      await resendVerification(email);
      setNotice('Si los datos son correctos, te enviamos otro correo.');
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      if (statusOf(err) === 429) setCooldown(RESEND_COOLDOWN_SECONDS);
      setError(signupErrorMessage(statusOf(err), codeOf(err), 'No fue posible reenviar el correo.'));
    } finally { setBusy(false); }
  };

  const continueHere = async () => {
    if (checking) return;
    setChecking(true); setError(undefined); setNotice(undefined);
    try {
      const view = await getSignupSession();
      if (view.status === 'PENDING_EMAIL_VERIFICATION') {
        setNotice('Todavía no vemos tu correo confirmado. Abre el enlace que te enviamos y vuelve aquí.');
        return;
      }
      if (onVerified) onVerified(); else goTo('signup/setup');
    } catch (err) {
      if (statusOf(err) !== 401) { setError(continueErrorMessage(statusOf(err))); return; }
      // Sin sesión de registro en este navegador: se sigue con correo y contraseña.
      signupHandoff.set({ status: 'PENDING_EMAIL_VERIFICATION', email });
      if (onNeedPassword) onNeedPassword(); else goTo('signup/continue');
    } finally { setChecking(false); }
  };

  return (
    <Step id="check-email" lead={<p>Te enviamos un enlace a <strong>{email}</strong>. Ábrelo para confirmar tu correo y preparar tu negocio.</p>} title="Revisa tu correo">
      <p className="n3-onb__note">¿No lo ves? Busca en spam o correo no deseado. El enlace vence en unos minutos; si vence, pide otro.</p>
      {notice && <Notice>{notice}</Notice>}
      {error && <Alert>{error}</Alert>}
      <div className="n3-onb__actions">
        <Button disabled={cooldown > 0} fullWidth loading={busy} loadingLabel="Reenviando…" onClick={() => void resend()} variant="secondary">
          {cooldown > 0 ? `Reenviar en ${cooldown} s` : 'Reenviar correo'}
        </Button>
        {onChangeEmail && <Button fullWidth onClick={onChangeEmail} variant="ghost">Corregir el correo</Button>}
      </div>
      <p className="n3-onb__foot">
        ¿Ya lo confirmaste? <TextLink onClick={() => void continueHere()}>{checking ? 'Comprobando…' : 'Continuar aquí'}</TextLink>
      </p>
    </Step>
  );
}

// ── 3. Abrir el enlace verifica el correo ──────────────────────────────────────────────────────

// ONB-002/ONB-003: abrir el enlace basta para confirmar el correo; no se pide nada. El servidor le da a
// ESTE navegador la única sesión de registro que sirve después de verificar:
//   - si es el navegador donde se creó la cuenta, sigue directo a preparar el negocio;
//   - si no (otro equipo, otro navegador), para seguir aquí se escribe la contraseña de la cuenta. La
//     ventana donde se creó la cuenta ya no puede seguir por su cuenta (eso es lo que impide que alguien que
//     registró un correo ajeno se quede con el registro cuando la dueña del correo abre el enlace).
//   - un enlace ya usado no da nada nuevo: se puede pedir uno nuevo con la contraseña.
type VerifyState =
  | { kind: 'verifying' }
  | { kind: 'failure'; message: string }
  | { kind: 'retry'; message: string }
  | { kind: 'provisioned' }
  | { kind: 'claim'; email: string }
  | { kind: 'used'; email: string };

function VerifyView({ token: tokenFromUrl }: { token: string | null }) {
  // El token se lee UNA vez. Después se borra de la dirección (abajo), y una lectura posterior de la
  // dirección —un `hashchange` tardío— no debe convertirlo en «falta el enlace».
  const [firstToken] = useState(tokenFromUrl);
  const token = firstToken ?? tokenFromUrl;
  const [state, setState] = useState<VerifyState>(token ? { kind: 'verifying' } : { kind: 'failure', message: verifyErrorMessage(-1, 'Falta el enlace de verificación.') });
  const started = useRef(false);

  useEffect(() => {
    if (!token) return;
    // Limpia el token de la URL visible (evita fuga por Referer/historial) una vez leído en memoria.
    try { history.replaceState(null, '', `${window.location.pathname}${window.location.search}#signup/verify`); } catch { /* noop */ }
  }, [token]);

  const verify = useCallback(async () => {
    if (!token) return;
    setState({ kind: 'verifying' });
    try {
      const result = await verifySignup(token);
      if (result.status === 'PROVISIONED') { setState({ kind: 'provisioned' }); return; }
      if (result.canContinue) {
        signupHandoff.set({ status: 'EMAIL_VERIFIED', email: result.email, justVerified: true });
        goTo('signup/setup');
        return;
      }
      setState(result.needsPassword ? { kind: 'claim', email: result.email } : { kind: 'used', email: result.email });
    } catch (err) {
      const status = statusOf(err);
      // Enlace inválido, vencido o ya usado (400) o registro cerrado (404): no hay nada que reintentar aquí.
      if (status === 400 || status === 404) setState({ kind: 'failure', message: verifyErrorMessage(status, err instanceof SelfServiceApiError ? err.message : undefined) });
      else setState({ kind: 'retry', message: verifyErrorMessage(status) });
    }
  }, [token]);

  // Se verifica al abrir, una sola vez (el doble montaje de desarrollo no lo repite).
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void verify();
  }, [verify]);

  if (state.kind === 'verifying') return <Loading label="Confirmando tu correo…" />;

  if (state.kind === 'failure') {
    return (
      <Step id="verify-error" lead={state.message} title="No pudimos confirmar tu correo">
        <div className="n3-onb__actions">
          <Button fullWidth onClick={() => goTo('signup/continue')} variant="primary">Ya lo confirmé: continuar</Button>
          <Button fullWidth onClick={() => goTo('signup')} variant="secondary">Crear cuenta de nuevo</Button>
        </div>
        <p className="n3-onb__foot">¿Ya tienes negocio? <TextLink onClick={goLogin}>Inicia sesión</TextLink></p>
      </Step>
    );
  }

  if (state.kind === 'retry') {
    return (
      <Step id="verify-retry" lead={state.message} title="No pudimos confirmar tu correo">
        <div className="n3-onb__actions"><Button fullWidth onClick={() => void verify()} size="lg" variant="primary">Reintentar</Button></div>
      </Step>
    );
  }

  if (state.kind === 'provisioned') {
    return (
      <Step id="verify-done" lead="Tu correo ya estaba confirmado y tu negocio ya existe. Entra con tu correo y tu contraseña." title="Tu negocio ya está listo">
        <div className="n3-onb__actions"><Button fullWidth onClick={goLogin} size="lg" variant="primary">Iniciar sesión</Button></div>
      </Step>
    );
  }

  if (state.kind === 'used') {
    return (
      <PasswordGate
        id="link-used"
        initialEmail={state.email}
        lead="Tu correo ya está confirmado. Si ya estabas preparando tu negocio en otra ventana, sigue ahí. Para seguir en este equipo, escribe tu contraseña y te enviaremos un enlace nuevo."
        title="Este enlace ya se usó"
      />
    );
  }

  return (
    <PasswordGate
      id="verified-elsewhere"
      initialEmail={state.email}
      lead="Para seguir en este equipo, escribe la contraseña que elegiste al crear tu cuenta."
      title="Tu correo quedó confirmado"
    />
  );
}

// ── 3b. ONB-010: la invitación a la beta privada ──────────────────────────────────────────────

// Abrir el enlace del correo: el servidor dice en qué punto está ESA invitación.
//   - pendiente: tu nombre y una contraseña (el correo es el de la invitación; no se escribe) → «Prepara tu negocio»;
//   - aceptada: si este navegador ya tiene la sesión, sigue directo; si no (otro equipo), con la contraseña elegida;
//   - con negocio: a iniciar sesión;
//   - vencida, revocada o inválida: un mensaje claro de qué hacer.
// El token se lee UNA vez y se borra de la dirección (como el de verificación).
type InviteState =
  | { kind: 'loading' }
  | { kind: 'missing' }
  | { kind: 'failure'; message: string }
  | { kind: 'retry'; message: string }
  | { kind: 'pending'; email: string }
  | { kind: 'claim'; email: string; displayName: string | null }
  | { kind: 'provisioned' };

function InviteView({ token: tokenFromUrl }: { token: string | null }) {
  const [firstToken] = useState(tokenFromUrl);
  const token = firstToken ?? tokenFromUrl;
  const [state, setState] = useState<InviteState>(token ? { kind: 'loading' } : { kind: 'missing' });
  const started = useRef(false);

  useEffect(() => {
    if (!token) return;
    try { history.replaceState(null, '', `${window.location.pathname}${window.location.search}#beta-invite`); } catch { /* noop */ }
  }, [token]);

  const open = useCallback(async () => {
    if (!token) return;
    setState({ kind: 'loading' });
    try {
      const view = await inspectInvite(token);
      if (view.status === 'PROVISIONED') { setState({ kind: 'provisioned' }); return; }
      if (view.status === 'ACCEPTED') {
        if (view.canContinue) { goTo('beta-invite/setup'); return; }
        setState({ kind: 'claim', email: view.email, displayName: view.displayName });
        return;
      }
      setState({ kind: 'pending', email: view.email });
    } catch (err) {
      const status = statusOf(err);
      const message = inviteErrorMessage(status, codeOf(err));
      setState(status === 0 || status >= 500 ? { kind: 'retry', message } : { kind: 'failure', message });
    }
  }, [token]);

  // Se abre una sola vez (el doble montaje de desarrollo no lo repite).
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void open();
  }, [open]);

  if (state.kind === 'loading') return <Loading label="Abriendo tu invitación…" />;
  if (state.kind === 'missing' || !token) return <InviteLinkNeeded />;
  if (state.kind === 'failure') {
    return (
      <Step id="invite-error" lead={state.message} title="No pudimos abrir tu invitación">
        <p className="n3-onb__foot">¿Ya tienes negocio? <TextLink onClick={goLogin}>Inicia sesión</TextLink></p>
      </Step>
    );
  }
  if (state.kind === 'retry') {
    return (
      <Step id="invite-retry" lead={state.message} title="No pudimos abrir tu invitación">
        <div className="n3-onb__actions"><Button fullWidth onClick={() => void open()} size="lg" variant="primary">Reintentar</Button></div>
      </Step>
    );
  }
  if (state.kind === 'provisioned') {
    return (
      <Step id="invite-done" lead="Tu negocio ya está creado. Entra con tu correo y tu contraseña." title="Tu negocio ya está listo">
        <div className="n3-onb__actions"><Button fullWidth onClick={goLogin} size="lg" variant="primary">Iniciar sesión</Button></div>
      </Step>
    );
  }
  if (state.kind === 'claim') return <InviteClaimForm email={state.email} onProvisioned={() => setState({ kind: 'provisioned' })} token={token} />;
  return <InviteAcceptForm email={state.email} onAlreadyAccepted={() => void open()} token={token} />;
}

function InviteAcceptForm({ token, email, onAlreadyAccepted }: { token: string; email: string; onAlreadyAccepted: () => void }) {
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const errors = {
    name: displayName.trim().length < 2 ? 'Escribe tu nombre (al menos 2 letras).' : undefined,
    password: accountPasswordProblem(password),
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setTried(true);
    if (errors.name || errors.password) return;
    setBusy(true); setError(undefined);
    try {
      const result = await acceptInvite({ token, displayName: displayName.trim(), password });
      setPassword(''); // la contraseña no se queda en la pantalla
      signupHandoff.set({ status: 'EMAIL_VERIFIED', email: result.email, justVerified: true });
      goTo('beta-invite/setup');
    } catch (err) {
      // Otra pestaña (o un doble envío) ya la aceptó: se vuelve a abrir el enlace para ver cómo seguir.
      if (codeOf(err) === 'PRIVATE_BETA_INVITE_ALREADY_ACCEPTED') { onAlreadyAccepted(); return; }
      setError(inviteErrorMessage(statusOf(err), codeOf(err), 'No pudimos crear tu cuenta. Inténtalo de nuevo.'));
    } finally { setBusy(false); }
  };

  return (
    <Step
      eyebrow="Beta privada"
      id="invite"
      lead={<p>Crea tu cuenta para preparar tu negocio. Durante la beta no tiene costo y no te pediremos tarjeta.</p>}
      title="Te invitaron a Negocio360"
    >
      <form className="n3-onb__form" noValidate onSubmit={(event) => void submit(event)}>
        {error && <Alert>{error}</Alert>}
        <Field hint="Es el correo de tu invitación; con él entrarás a tu negocio." label="Correo">
          <Input readOnly type="email" value={email} />
        </Field>
        <Field error={tried ? errors.name : undefined} label="Tu nombre" required>
          <Input autoComplete="name" maxLength={120} onChange={(event) => setDisplayName(event.target.value)} value={displayName} />
        </Field>
        <PasswordField autoComplete="new-password" error={tried ? errors.password : undefined} onChange={setPassword} value={password} />
        <div className="n3-onb__actions">
          <Button fullWidth loading={busy} loadingLabel="Creando tu cuenta…" size="lg" type="submit" variant="primary">Crear mi cuenta</Button>
        </div>
      </form>
      <p className="n3-onb__foot">¿Ya tienes negocio? <TextLink onClick={goLogin}>Inicia sesión</TextLink></p>
    </Step>
  );
}

/** ONB-010: la cuenta ya se creó con esta invitación (en otro equipo o navegador): se sigue con su contraseña. */
function InviteClaimForm({ token, email, onProvisioned }: { token: string; email: string; onProvisioned: () => void }) {
  const [password, setPassword] = useState('');
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const passwordError = password ? undefined : 'Escribe tu contraseña.';

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setTried(true);
    if (passwordError) return;
    setBusy(true); setError(undefined);
    try {
      const result = await claimInvite(token, password);
      setPassword('');
      signupHandoff.set({ status: 'EMAIL_VERIFIED', email: result.email });
      goTo('beta-invite/setup');
    } catch (err) {
      if (codeOf(err) === 'PRIVATE_BETA_INVITE_ALREADY_PROVISIONED') { onProvisioned(); return; }
      setError(inviteErrorMessage(statusOf(err), codeOf(err), 'No pudimos continuar. Inténtalo de nuevo.'));
    } finally { setBusy(false); }
  };

  return (
    <Step id="invite-claim" lead={<p>Ya creaste tu cuenta con <strong>{email}</strong>. Para seguir en este equipo, escribe la contraseña que elegiste.</p>} title="Sigue preparando tu negocio">
      <form className="n3-onb__form" noValidate onSubmit={(event) => void submit(event)}>
        {error && <Alert>{error}</Alert>}
        <PasswordField autoComplete="current-password" autoFocus error={tried ? passwordError : undefined} onChange={setPassword} value={password} />
        <div className="n3-onb__actions">
          <Button fullWidth loading={busy} loadingLabel="Comprobando…" size="lg" type="submit" variant="primary">Continuar</Button>
        </div>
      </form>
      <p className="n3-onb__foot">¿Ya tienes negocio? <TextLink onClick={goLogin}>Inicia sesión</TextLink></p>
    </Step>
  );
}

// ── 4. Continuar con correo + contraseña (reanudar) ────────────────────────────────────────────

/**
 * ONB-003: el correo ya se confirmó y este navegador no abrió el enlace. Por seguridad la contraseña sola
 * no basta: el servidor envió un enlace nuevo al correo, que hay que abrir en este equipo.
 */
function LinkRequiredView({ email, onRetry }: { email: string; onRetry: () => void }) {
  return (
    <Step
      id="link-required"
      lead={<p>Por seguridad, para seguir en este equipo abre aquí el enlace que te enviamos a <strong>{email}</strong>.</p>}
      title="Abre el enlace en este equipo"
    >
      <p className="n3-onb__note">Si ya estabas preparando tu negocio en otra ventana o equipo, puedes seguir ahí. ¿No ves el correo? Busca en spam o correo no deseado.</p>
      <div className="n3-onb__actions"><Button fullWidth onClick={onRetry} variant="secondary">Pedir otro enlace</Button></div>
      <p className="n3-onb__foot">¿Ya tienes negocio? <TextLink onClick={goLogin}>Inicia sesión</TextLink></p>
    </Step>
  );
}

/**
 * Pide el correo y la contraseña de la cuenta y lleva a donde toque: a preparar el negocio (correo
 * confirmado y este navegador abrió el enlace), a «Revisa tu correo» (todavía sin confirmar) o a «Abre el
 * enlace en este equipo» (confirmado en otro lado). `onVerified` sustituye a la navegación cuando la
 * pantalla YA es la de preparar el negocio (el mismo hash no avisaría a nadie).
 */
function PasswordGate({ id, title, lead, notice, initialEmail, onVerified }: {
  id: string; title: string; lead: ReactNode; notice?: string; initialEmail?: string; onVerified?: () => void;
}) {
  const [email, setEmail] = useState(initialEmail ?? signupHandoff.peek()?.email ?? '');
  const [password, setPassword] = useState('');
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [linkEmail, setLinkEmail] = useState<string | null>(null);

  const errors = {
    email: !EMAIL_PATTERN.test(email.trim()) ? 'Escribe un correo válido.' : undefined,
    password: password ? undefined : 'Escribe tu contraseña.',
  };
  const verified = onVerified ?? (() => goTo('signup/setup'));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setTried(true);
    if (errors.email || errors.password) return;
    setBusy(true); setError(undefined);
    try {
      const result = await continueSignup(email.trim(), password);
      setPassword('');
      if (result.status === 'PENDING_EMAIL_VERIFICATION') { setPendingEmail(result.email); return; }
      signupHandoff.set({ status: 'EMAIL_VERIFIED', email: result.email });
      verified();
    } catch (err) {
      if (codeOf(err) === 'SIGNUP_LINK_REQUIRED') { setPassword(''); setTried(false); setLinkEmail(email.trim()); return; }
      setError(continueErrorMessage(statusOf(err)));
    } finally { setBusy(false); }
  };

  if (pendingEmail) return <CheckEmailView email={pendingEmail} onNeedPassword={() => setPendingEmail(null)} onVerified={verified} />;
  if (linkEmail) return <LinkRequiredView email={linkEmail} onRetry={() => setLinkEmail(null)} />;

  return (
    <Step id={id} lead={lead} title={title}>
      <form className="n3-onb__form" noValidate onSubmit={(event) => void submit(event)}>
        {notice && <Notice>{notice}</Notice>}
        {error && <Alert>{error}</Alert>}
        <Field error={tried ? errors.email : undefined} label="Correo" required>
          <Input autoComplete="email" inputMode="email" maxLength={200} onChange={(event) => setEmail(event.target.value)} type="email" value={email} />
        </Field>
        <PasswordField autoComplete="current-password" autoFocus={Boolean(initialEmail)} error={tried ? errors.password : undefined} onChange={setPassword} value={password} />
        <div className="n3-onb__actions">
          <Button fullWidth loading={busy} loadingLabel="Comprobando…" size="lg" type="submit" variant="primary">Continuar</Button>
        </div>
      </form>
      <p className="n3-onb__foot">¿Ya tienes negocio? <TextLink onClick={goLogin}>Inicia sesión</TextLink></p>
    </Step>
  );
}

function ContinueView() {
  // Viene del inicio de sesión: con una solicitud que aún no confirma su correo, primero «Revisa tu
  // correo»; con una ya confirmada en otro lado (ONB-003), «Abre el enlace en este equipo». En ambos casos
  // se puede volver a pedir correo y contraseña sin cambiar de dirección.
  const [mode, setMode] = useState<'check' | 'link' | 'gate'>(() => {
    const handoff = signupHandoff.peek();
    if (handoff?.status === 'PENDING_EMAIL_VERIFICATION' && handoff.showCheckEmail) return 'check';
    if (handoff?.status === 'EMAIL_VERIFIED' && handoff.linkRequired) return 'link';
    return 'gate';
  });
  const handoff = signupHandoff.peek();
  if (mode === 'check' && handoff) return <CheckEmailView email={handoff.email} onNeedPassword={() => setMode('gate')} />;
  if (mode === 'link' && handoff) return <LinkRequiredView email={handoff.email} onRetry={() => setMode('gate')} />;
  return <PasswordGate id="continue" lead="Escribe el correo y la contraseña con los que te registraste para seguir donde te quedaste." title="Continúa con tu registro" />;
}

// ── 5. Bienvenida + preparar el negocio ────────────────────────────────────────────────────────

type SetupState =
  | { kind: 'loading' }
  | { kind: 'gate'; notice?: string }
  | { kind: 'claim'; email: string }
  | { kind: 'pending'; email: string }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; registration: RegistrationView };

/**
 * ONB-010: el MISMO «Prepara tu negocio» sirve al registro guiado y a la invitación a la beta privada; solo cambian
 * las rutas de la API. En la invitación no hay «entra con correo y contraseña»: sin sesión, se vuelve al enlace.
 */
interface SetupFlow {
  kind: 'signup' | 'invite';
  getSession: () => Promise<SessionView>;
  setup: (body: SetupBusinessBody) => Promise<SetupBusinessResult>;
}
const SIGNUP_FLOW: SetupFlow = { kind: 'signup', getSession: getSignupSession, setup: setupBusiness };
const INVITE_FLOW: SetupFlow = { kind: 'invite', getSession: getInviteSession, setup: setupInviteBusiness };

/** Invitación sin sesión en este navegador: se sigue abriendo de nuevo el enlace del correo. */
function InviteLinkNeeded({ notice }: { notice?: string }) {
  return (
    <Step id="invite-link-needed" lead="Para seguir, abre de nuevo el enlace de tu invitación. Está en el correo que te enviamos." title="Abre tu invitación">
      {notice && <Notice>{notice}</Notice>}
      <p className="n3-onb__foot">¿Ya tienes negocio? <TextLink onClick={goLogin}>Inicia sesión</TextLink></p>
    </Step>
  );
}

function SetupView({ flow = SIGNUP_FLOW }: { flow?: SetupFlow }) {
  const [state, setState] = useState<SetupState>({ kind: 'loading' });
  const [step, setStep] = useState<'welcome' | 'business'>('welcome');
  // Llega de abrir el enlace (o de aceptar la invitación) en este navegador: la bienvenida lo confirma.
  const [justVerified] = useState(() => signupHandoff.peek()?.justVerified === true);

  // El servidor dice en qué punto está la solicitud de la sesión corta de este navegador. Recargar la
  // página no obliga a escribir nada otra vez mientras la sesión siga viva.
  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const registration = await flow.getSession();
      // Ya se creó (doble toque, respuesta perdida). La guía sigue si este navegador recibió la sesión del
      // dueño; si no, la guía pide iniciar sesión: la sesión corta de registro nunca abre una del dueño.
      if (registration.status === 'PROVISIONED') { goTo('onboarding'); return; }
      if (registration.status === 'PENDING_EMAIL_VERIFICATION') { setState({ kind: 'pending', email: registration.email }); return; }
      // ONB-003: este navegador abrió el enlace pero no inició el registro: falta la contraseña.
      if (registration.needsPassword) { setState({ kind: 'claim', email: registration.email }); return; }
      setState({ kind: 'ready', registration });
    } catch (err) {
      if (statusOf(err) === 401) { setState({ kind: 'gate' }); return; }
      setState({ kind: 'error', message: flow.kind === 'invite' ? inviteErrorMessage(statusOf(err), codeOf(err), setupErrorMessage(statusOf(err))) : setupErrorMessage(statusOf(err)) });
    }
  }, [flow]);
  useEffect(() => { void load(); }, [load]);

  if (state.kind === 'loading') return <Loading />;
  if (flow.kind === 'invite' && (state.kind === 'gate' || state.kind === 'claim' || state.kind === 'pending')) {
    return <InviteLinkNeeded notice={state.kind === 'gate' ? state.notice : undefined} />;
  }
  if (state.kind === 'gate') {
    return <PasswordGate id="setup-gate" lead="Para seguir, escribe el correo y la contraseña con los que te registraste." notice={state.notice} onVerified={() => void load()} title="Continúa con tu registro" />;
  }
  if (state.kind === 'claim') {
    return <PasswordGate id="setup-claim" initialEmail={state.email} lead="Para seguir en este equipo, escribe la contraseña que elegiste al crear tu cuenta." onVerified={() => void load()} title="Tu correo quedó confirmado" />;
  }
  if (state.kind === 'pending') return <CheckEmailView email={state.email} onNeedPassword={() => setState({ kind: 'gate' })} onVerified={() => void load()} />;
  if (state.kind === 'error') {
    return (
      <Step id="setup-error" lead={state.message} title="No pudimos continuar">
        <div className="n3-onb__actions"><Button fullWidth onClick={() => void load()} size="lg" variant="primary">Reintentar</Button></div>
      </Step>
    );
  }

  if (step === 'welcome') {
    const name = firstNameOf(state.registration.displayName);
    return (
      <Step eyebrow={name ? `Hola, ${name}` : undefined} id="welcome" title="Vamos a preparar tu negocio">
        {justVerified && <Notice>{flow.kind === 'invite' ? 'Tu cuenta quedó creada.' : 'Tu correo quedó confirmado.'}</Notice>}
        <div className="n3-onb__lead">
          <p>Solo te preguntaremos lo necesario.</p>
          <p>Lo demás podrás completarlo después.</p>
        </div>
        <div className="n3-onb__actions"><Button fullWidth onClick={() => setStep('business')} size="lg" variant="primary">Empezar</Button></div>
      </Step>
    );
  }

  return (
    <BusinessForm
      flow={flow}
      onSessionLost={() => setState({ kind: 'gate', notice: flow.kind === 'invite' ? 'Tu sesión para preparar el negocio terminó.' : 'Para seguir, vuelve a entrar con tu correo y tu contraseña.' })}
      registration={state.registration}
    />
  );
}

function BusinessForm({ registration, onSessionLost, flow }: { registration: RegistrationView; onSessionLost: () => void; flow: SetupFlow }) {
  const [businessName, setBusinessName] = useState(registration.businessName ?? '');
  const [storeName, setStoreName] = useState(registration.businessName ?? '');
  const [storeEdited, setStoreEdited] = useState(false);
  const [detected] = useState(detectTimezone);
  const [timezone, setTimezone] = useState(() => suggestTimezone(detected));
  // ONB-002: país y moneda son campos reales, con la sugerencia del idioma y la zona del equipo. Solo se
  // ofrece lo que Negocio360 soporta de verdad (ver locale.ts).
  const [countryCode, setCountryCode] = useState(() => suggestCountry(typeof navigator === 'undefined' ? undefined : navigator.language, detected));
  const [currencyCode, setCurrencyCode] = useState(() => countryOption(countryCode).defaultCurrency);
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const errors = {
    business: businessName.trim().length < 2 ? 'Escribe el nombre de tu negocio (al menos 2 letras).' : undefined,
    store: storeName.trim().length < 1 ? 'Escribe cómo se llama tu sucursal.' : undefined,
  };

  const changeCountry = (code: string) => {
    setCountryCode(code);
    setCurrencyCode(countryOption(code).defaultCurrency); // la moneda propuesta es la del país elegido
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setTried(true);
    if (errors.business || errors.store) return;
    setBusy(true); setError(undefined);
    try {
      await flow.setup({ registrationId: registration.registrationId, businessName: businessName.trim(), countryCode, currencyCode, storeName: storeName.trim(), timezone });
      signupHandoff.clear(); // desde aquí manda la sesión del dueño
      goTo('onboarding');
    } catch (err) {
      if (statusOf(err) === 401) { onSessionLost(); return; }
      // Ya estaba creado (respuesta perdida, doble envío): la guía sigue si este navegador tiene la sesión
      // del dueño; si no, pide iniciar sesión. Repetir el setup no abre otra sesión del dueño.
      if (codeOf(err) === 'SIGNUP_ALREADY_PROVISIONED') { signupHandoff.clear(); goTo('onboarding'); return; }
      // ONB-010: la invitación se revocó mientras preparaba su negocio.
      if (codeOf(err) === 'PRIVATE_BETA_INVITE_REVOKED') { setError(inviteErrorMessage(statusOf(err), codeOf(err))); return; }
      setError(setupErrorMessage(statusOf(err), codeOf(err)));
    } finally { setBusy(false); }
  };

  return (
    <Step id="business" lead="Con esto ya puedes vender. Podrás agregar más sucursales y datos después." title="Prepara tu negocio">
      <form className="n3-onb__form" noValidate onSubmit={(event) => void submit(event)}>
        {error && <Alert>{error}</Alert>}
        <Field error={tried ? errors.business : undefined} label="Nombre de tu negocio" required>
          <Input
            autoComplete="organization"
            maxLength={150}
            onChange={(event) => { setBusinessName(event.target.value); if (!storeEdited) setStoreName(event.target.value); }}
            value={businessName}
          />
        </Field>
        <Field error={tried ? errors.store : undefined} hint="Es donde registrarás tus ventas. Si solo tienes una, puede llamarse igual que tu negocio." label="Nombre de tu primera sucursal" required>
          <Input maxLength={120} onChange={(event) => { setStoreName(event.target.value); setStoreEdited(true); }} value={storeName} />
        </Field>
        <Field label="País" required>
          <Select autoComplete="country" onChange={(event) => changeCountry(event.target.value)} value={countryCode}>
            {SUPPORTED_COUNTRIES.map((country) => <option key={country.code} value={country.code}>{country.label}</option>)}
          </Select>
        </Field>
        <Field hint={`Es la moneda de tus precios, ventas y cortes. ${LOCALE_SCOPE_NOTE}`} label="Moneda" required>
          <Select onChange={(event) => setCurrencyCode(event.target.value)} value={currencyCode}>
            {currencyChoices(countryCode).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </Select>
        </Field>
        <Field hint={timezoneHint(detected)} label="Zona horaria" required>
          <Select onChange={(event) => setTimezone(event.target.value)} value={timezone}>
            {timezoneChoices(detected).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </Select>
        </Field>
        <div className="n3-onb__actions">
          <Button fullWidth loading={busy} loadingLabel="Preparando tu negocio…" size="lg" type="submit" variant="primary">Crear mi negocio</Button>
        </div>
      </form>
    </Step>
  );
}

// ── 6. Después de crear el negocio (con sesión) ────────────────────────────────────────────────

function OnboardingView() {
  const [status, setStatus] = useState<OnboardingStatus | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'auth' | 'error'>('loading');

  const load = useCallback(async () => {
    setState('loading');
    try { setStatus(await getOnboarding()); setState('ready'); }
    catch (err) { setState(statusOf(err) === 401 ? 'auth' : 'error'); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  if (state === 'loading') return <Card className="n3-onb__panel"><LoadingPanel label="Cargando…" lines={4} /></Card>;
  if (state === 'auth') {
    return (
      <Step id="auth" lead="Inicia sesión para continuar con la preparación de tu negocio." title="Inicia sesión">
        <div className="n3-onb__actions"><Button fullWidth onClick={goLogin} size="lg" variant="primary">Ir a iniciar sesión</Button></div>
      </Step>
    );
  }
  if (state === 'error' || !status) {
    return (
      <Step id="load-error" lead="No fue posible cargar tu configuración." title="Algo falló">
        <div className="n3-onb__actions"><Button fullWidth onClick={() => void load()} variant="primary">Reintentar</Button></div>
      </Step>
    );
  }
  // ONB-001: el negocio nació con el registro guiado (v2) → activación. Si no (v1: negocios creados por
  // Plataforma), el asistente de primera tienda de 27B, con el mismo comportamiento de siempre.
  if (status.version >= 2) return <GuidedActivation />;
  return <FirstStoreAssistant onStatus={setStatus} status={status} />;
}

const OFFERING_CHOICES: ReadonlyArray<{ value: BusinessOffering; title: string; text: string; icon: 'inventory' | 'users' | 'sell' }> = [
  { value: 'PRODUCTS', title: 'Productos', text: 'Lo que entregas: comida, bebidas, mercancía.', icon: 'inventory' },
  { value: 'SERVICES', title: 'Servicios', text: 'Lo que haces para tus clientes: cortes, clases, reparaciones.', icon: 'users' },
  { value: 'BOTH', title: 'Productos y servicios', text: 'Un poco de los dos.', icon: 'sell' },
];

function GuidedActivation() {
  const [activation, setActivation] = useState<Activation | null>(null);
  const [loadError, setLoadError] = useState<'owner' | 'other' | null>(null);
  const [skipping, setSkipping] = useState(false);
  const [saving, setSaving] = useState<BusinessOffering | null>(null);
  const [error, setError] = useState<string>();

  const load = useCallback(async () => {
    setLoadError(null);
    try { setActivation(await getActivation()); }
    catch (err) {
      const owner = err instanceof ApiError && err.status === 403;
      setLoadError(owner ? 'owner' : 'other');
      if (!owner) noteOnboardingError('activation', err);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const choose = async (offering: BusinessOffering) => {
    if (saving) return;
    setSaving(offering); setError(undefined);
    try { setActivation(await setOffering(offering)); }
    catch (err) { setError('No pudimos guardar tu respuesta. Inténtalo de nuevo.'); noteOnboardingError('offering', err); }
    finally { setSaving(null); }
  };

  // P-085 (Codex R1, K5): «Lo haré después» se GUARDA en el servidor antes de seguir; si no se pudo, se dice y la persona
  // decide (reintentar o crear el artículo). Guardado, lleva al Inicio, donde el checklist ofrece crearlo cuando quiera.
  const skip = async () => {
    if (skipping) return;
    setSkipping(true); setError(undefined);
    try {
      await skipFirstItem();
      goTo('dashboard');
    } catch (err) {
      setError('No pudimos guardar tu respuesta. Inténtalo de nuevo o agrega tu primer artículo.');
      noteOnboardingError('first_item', err);
    } finally {
      setSkipping(false);
    }
  };

  if (loadError === 'owner') {
    return (
      <Step id="owner-only" lead="La preparación del negocio la hace su dueño." title="Esta guía es para el dueño">
        <div className="n3-onb__actions"><Button fullWidth onClick={() => goTo('dashboard')} size="lg" variant="primary">Ir a Inicio</Button></div>
      </Step>
    );
  }
  if (loadError) {
    return (
      <Step id="activation-error" lead="Tu negocio ya está creado. Puedes reintentar o seguir desde el Inicio." title="No pudimos cargar el siguiente paso">
        <div className="n3-onb__actions">
          <Button fullWidth onClick={() => void load()} variant="primary">Reintentar</Button>
          <Button fullWidth onClick={() => goTo('dashboard')} variant="secondary">Ir a Inicio</Button>
        </div>
      </Step>
    );
  }
  if (!activation) return <Card className="n3-onb__panel"><LoadingPanel label="Cargando…" lines={4} /></Card>;

  if (activation.offering === null) {
    return (
      <Step eyebrow="Tu negocio ya está creado" id="offering" lead="Así te mostramos solo lo que te sirve." title="¿Qué vas a vender principalmente?">
        {error && <Alert>{error}</Alert>}
        <div aria-label="Qué vas a vender" className="n3-onb__choices" role="group">
          {OFFERING_CHOICES.map((choice) => (
            <button
              aria-busy={saving === choice.value || undefined}
              className="n3-onb__choice"
              disabled={saving !== null}
              key={choice.value}
              onClick={() => void choose(choice.value)}
              type="button"
            >
              <span aria-hidden="true" className="n3-onb__choice-icon"><Icon name={choice.icon} size={22} /></span>
              <span className="n3-onb__choice-text">
                <span className="n3-onb__choice-title">{choice.title}</span>
                <span className="n3-onb__choice-detail">{saving === choice.value ? 'Guardando…' : choice.text}</span>
              </span>
            </button>
          ))}
        </div>
      </Step>
    );
  }

  const noun = offeringNoun(activation.offering);
  if (!activation.hasSellableItem && !activation.firstItemSkipped) {
    return (
      <Step id="first-item" lead="Solo el nombre y el precio. Podrás cobrarlo de inmediato en el punto de venta." title={`Agrega tu primer ${noun}`}>
        {error && <Alert>{error}</Alert>}
        <QuickProductForm
          offering={activation.offering}
          // P-085 (decisión aprobada): tras el primer artículo se abre el punto de venta. Nada obliga a vender: la primera
          // venta llega cuando hay un cliente de verdad.
          onCreated={() => goTo(POS_HASH)}
          onFailed={(failure) => noteOnboardingErrorCode('first_item', failure.uncertain ? 'NETWORK_ERROR' : 'PRODUCT_CREATE_FAILED')}
          secondaryAction={<Button fullWidth loading={skipping} loadingLabel="Guardando…" onClick={() => void skip()} variant="ghost">Lo haré después</Button>}
        />
      </Step>
    );
  }

  return (
    <Step
      id="ready"
      lead={activation.hasSellableItem
        ? 'Tu negocio está listo y ya tienes algo que vender. Cuando quieras, haz tu primera venta.'
        : `Tu negocio está listo. Cuando quieras, agrega tu primer ${noun} desde el Inicio.`}
      title="Ya puedes empezar"
    >
      <span aria-hidden="true" className="n3-onb__ready-icon"><Icon name="success" size={36} /></span>
      <div className="n3-onb__actions">
        {activation.hasSellableItem && <Button fullWidth onClick={() => goTo(POS_HASH)} size="lg" variant="primary">Abrir punto de venta</Button>}
        <Button fullWidth onClick={() => goTo('dashboard')} size={activation.hasSellableItem ? 'md' : 'lg'} variant={activation.hasSellableItem ? 'secondary' : 'primary'}>Ir a Inicio</Button>
      </div>
    </Step>
  );
}

// Fase 27B (v1): negocios creados por Plataforma, sin tienda todavía. Mismo comportamiento de siempre.
function FirstStoreAssistant({ status, onStatus }: { status: OnboardingStatus; onStatus: (status: OnboardingStatus) => void }) {
  const [detected] = useState(detectTimezone);
  const [storeName, setStoreName] = useState('');
  const [timezone, setTimezone] = useState(() => suggestTimezone(detected));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const hasStore = status.completedSteps.includes('FIRST_STORE_READY');

  const run = async (action: () => Promise<OnboardingStatus>, fallback: string) => {
    setBusy(true); setError(undefined);
    try { onStatus(await action()); }
    catch (err) { setError(err instanceof SelfServiceApiError ? err.message : fallback); }
    finally { setBusy(false); }
  };

  if (status.status === 'COMPLETED') {
    return (
      <Step id="v1-done" lead="Ya puedes empezar a registrar tu operación en Negocio360." title="Tu negocio está listo">
        <span aria-hidden="true" className="n3-onb__ready-icon"><Icon name="success" size={36} /></span>
        <div className="n3-onb__actions"><Button fullWidth onClick={goLogin} size="lg" variant="primary">Entrar a Negocio360</Button></div>
      </Step>
    );
  }
  if (hasStore) {
    return (
      <Step id="v1-finish" lead="Tu tienda quedó creada. Finaliza para empezar a usar Negocio360." title="Todo listo para terminar">
        {error && <Alert>{error}</Alert>}
        <div className="n3-onb__actions">
          <Button fullWidth loading={busy} loadingLabel="Finalizando…" onClick={() => void run(completeOnboarding, 'No fue posible finalizar.')} size="lg" variant="primary">Finalizar configuración</Button>
        </div>
      </Step>
    );
  }
  return (
    <Step id="v1-store" lead="La tienda es donde registrarás tus ventas y turnos. Puedes agregar más después." title="Crea tu primera tienda">
      <form className="n3-onb__form" noValidate onSubmit={(event) => { event.preventDefault(); if (storeName.trim()) void run(() => configureFirstStore({ name: storeName.trim(), timezone }), 'No fue posible crear la tienda.'); }}>
        {error && <Alert>{error}</Alert>}
        <Field label="Nombre de la tienda" required>
          <Input maxLength={120} onChange={(event) => setStoreName(event.target.value)} value={storeName} />
        </Field>
        <Field hint={timezoneHint(detected)} label="Zona horaria" required>
          <Select onChange={(event) => setTimezone(event.target.value)} value={timezone}>
            {timezoneChoices(detected).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </Select>
        </Field>
        <div className="n3-onb__actions">
          <Button disabled={!storeName.trim()} fullWidth loading={busy} loadingLabel="Creando…" size="lg" type="submit" variant="primary">Crear tienda</Button>
        </div>
      </form>
    </Step>
  );
}
